/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { PageEvent } from "@angular/material/paginator";
import { patchState, signalStoreFeature, withMethods, withState } from "@ngrx/signals";
import { StoreCollection } from "src/app/enums";

export interface PaginationState {
  pageSize: number;
  pageCount: number;
  page: number;
  total: number;
}

export interface PaginationStoreState<K extends StoreCollection> {
  paginations: Record<K, PaginationState>;
}

export interface SetPaginationParams {
  pageSize?: number;
  pageCount?: number;
  page?: number;
  total?: number;
}

export function withPagination<K extends StoreCollection>(initialState: PaginationStoreState<K>, hasOffsetIndex = true) {
  const getPaginationState = (paginations: PaginationStoreState<K>["paginations"], collection: K): PaginationState =>
    paginations[collection] ?? initialState.paginations[collection];

  return signalStoreFeature(
    withState<PaginationStoreState<K>>(initialState),
    withMethods(store => ({
      setPagination(params: SetPaginationParams, collection: K): void {
        const current = getPaginationState(store.paginations(), collection);
        patchState(store, {
          paginations: {
            ...store.paginations(),
            [collection]: {
              pageSize: params.pageSize ?? current.pageSize,
              pageCount: params.pageCount ?? current.pageCount,
              page: params.page ?? current.page,
              total: params.total ?? current.total,
            },
          },
        });
      },
      resetPagination(collection: K): void {
        patchState(store, {
          paginations: {
            ...store.paginations(),
            [collection]: initialState.paginations[collection],
          },
        });
      },
      setPaginationFromEvent(params: PageEvent, collection: K): void {
        const current = getPaginationState(store.paginations(), collection);
        patchState(store, {
          paginations: {
            ...store.paginations(),
            [collection]: {
              ...current,
              pageSize: params.pageSize,
              page: hasOffsetIndex ? params.pageIndex + 1 : params.pageIndex,
            },
          },
        });
      },
    })),
  );
}
